// Fund Detail - Full fund profile with historical price chart
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '@/lib/api';
import { OrderModal } from '@/components/OrderModal';

export const FundDetail = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [fund, setFund] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartRange, setChartRange] = useState('1M'); // 1M, 3M, 1Y, 5Y
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  useEffect(() => {
    if (symbol) {
      loadFundDetails();
    }
  }, [symbol]);

  useEffect(() => {
    if (symbol) {
      loadHistoricalData(chartRange);
    }
  }, [chartRange, symbol]);

  const loadFundDetails = async () => {
    try {
      setLoading(true);
      setError('');

      // Get fund from approved funds list
      const fundsRes = await api.get('/investments/funds');
      const foundFund = fundsRes.data.funds.find(f => f.symbol === symbol);

      if (!foundFund) {
        setError('Fund not found');
        return;
      }

      setFund(foundFund);
      setQuote({
        price: foundFund.current_price,
        change: foundFund.change,
        change_percent: foundFund.change_percent
      });

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load fund details');
      console.error('Fund detail load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadHistoricalData = async (range) => {
    try {
      // For now, generate mock historical data
      // In production, this would call a backend endpoint that fetches from Polygon.io
      const days = {
        '1M': 30,
        '3M': 90,
        '1Y': 365,
        '5Y': 1825
      }[range] || 30;

      const data = [];
      const basePrice = quote?.price || 100;
      const now = new Date();

      for (let i = days; i >= 0; i--) {
        const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
        const trend = (days - i) * 0.05; // Slight upward trend
        const noise = (Math.random() - 0.5) * 2; // Random fluctuation
        const price = basePrice - trend + noise;

        data.push({
          date: date.toISOString().split('T')[0],
          price: parseFloat(price.toFixed(2)),
          displayDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        });
      }

      setHistoricalData(data);

    } catch (err) {
      console.error('Historical data load error:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    const formatted = (value || 0).toFixed(2);
    return value >= 0 ? `+${formatted}%` : `${formatted}%`;
  };

  const getRiskColor = (rating) => {
    const colors = {
      'Very Low': 'bg-[#28A745] text-white',
      'Low': 'bg-[#34D058] text-[#24292E]',
      'Moderate': 'bg-[#0366D6] text-white',
      'Moderate-High': 'bg-[#FD7E14] text-white',
      'High': 'bg-[#D73A49] text-white'
    };
    return colors[rating] || 'bg-[#6A737D] text-white';
  };

  const handleInvest = () => {
    setOrderModalOpen(true);
  };

  const handleOrderComplete = () => {
    setOrderModalOpen(false);
    navigate('/investments');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <p className="text-[#586069]">Loading fund details...</p>
      </div>
    );
  }

  if (error || !fund) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#FFF5F5] border border-[#FEB2B2] rounded px-4 py-3 text-[#C53030]">
            {error || 'Fund not found'}
          </div>
          <button
            onClick={() => navigate('/funds')}
            className="mt-4 px-4 py-2 bg-[#0366D6] hover:bg-[#0256C5] text-white text-sm font-medium rounded transition-colors"
          >
            Back to Funds
          </button>
        </div>
      </div>
    );
  }

  // Calculate 52-week high/low from historical data
  const prices = historicalData.map(d => d.price);
  const fiftyTwoWeekHigh = prices.length > 0 ? Math.max(...prices) : quote.price;
  const fiftyTwoWeekLow = prices.length > 0 ? Math.min(...prices) : quote.price;

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E1E4E8]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/funds')}
            className="flex items-center gap-2 text-sm text-[#0366D6] hover:text-[#0256C5] mb-3 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Funds
          </button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-[#24292E] font-mono">{fund.symbol}</h1>
                <span className={`px-3 py-1 text-sm font-semibold rounded ${getRiskColor(fund.risk_rating)}`}>
                  {fund.risk_rating}
                </span>
              </div>
              <h2 className="text-xl text-[#586069] mb-2">{fund.name}</h2>
              <p className="text-sm text-[#6A737D]">{fund.asset_class}</p>
            </div>

            <button
              onClick={handleInvest}
              className="px-6 py-3 bg-[#0366D6] hover:bg-[#0256C5] text-white font-semibold rounded transition-colors"
            >
              Invest in {fund.symbol}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Price and Chart */}
          <div className="lg:col-span-2 space-y-6">
            {/* Current Price Card */}
            <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
              <div className="flex items-baseline gap-4 mb-2">
                <span className="text-4xl font-bold text-[#24292E]">
                  {formatCurrency(quote.price)}
                </span>
                <span className={`text-xl font-semibold ${
                  quote.change >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                }`}>
                  {formatCurrency(quote.change)} ({formatPercent(quote.change_percent)})
                </span>
              </div>
              <p className="text-sm text-[#586069]">As of {new Date().toLocaleString()}</p>
            </div>

            {/* Historical Chart */}
            <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#24292E]">Price History</h3>
                <div className="flex gap-2">
                  {['1M', '3M', '1Y', '5Y'].map((range) => (
                    <button
                      key={range}
                      onClick={() => setChartRange(range)}
                      className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                        chartRange === range
                          ? 'bg-[#0366D6] text-white'
                          : 'bg-[#F6F8FA] text-[#586069] hover:bg-[#E1E4E8]'
                      }`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>

              {historicalData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={historicalData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E1E4E8" />
                    <XAxis 
                      dataKey="displayDate" 
                      stroke="#6A737D"
                      tick={{ fontSize: 12 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      stroke="#6A737D"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `$${value.toFixed(2)}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E1E4E8',
                        borderRadius: '4px'
                      }}
                      formatter={(value) => [formatCurrency(value), 'Price']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke="#0366D6" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-96 flex items-center justify-center text-[#586069]">
                  Loading chart data...
                </div>
              )}
            </div>

            {/* Description */}
            <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
              <h3 className="text-lg font-semibold text-[#24292E] mb-3">About This Fund</h3>
              <p className="text-[#586069] leading-relaxed">
                {fund.description}
              </p>
            </div>
          </div>

          {/* Right Column - Key Stats */}
          <div className="space-y-6">
            {/* Key Statistics */}
            <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
              <h3 className="text-lg font-semibold text-[#24292E] mb-4">Key Statistics</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-[#E1E4E8]">
                  <span className="text-sm text-[#586069]">52-Week High</span>
                  <span className="text-sm font-semibold text-[#24292E]">
                    {formatCurrency(fiftyTwoWeekHigh)}
                  </span>
                </div>

                <div className="flex items-center justify-between pb-3 border-b border-[#E1E4E8]">
                  <span className="text-sm text-[#586069]">52-Week Low</span>
                  <span className="text-sm font-semibold text-[#24292E]">
                    {formatCurrency(fiftyTwoWeekLow)}
                  </span>
                </div>

                <div className="flex items-center justify-between pb-3 border-b border-[#E1E4E8]">
                  <span className="text-sm text-[#586069]">Expense Ratio</span>
                  <span className="text-sm font-semibold text-[#24292E]">
                    {fund.expense_ratio}%
                  </span>
                </div>

                <div className="flex items-center justify-between pb-3 border-b border-[#E1E4E8]">
                  <span className="text-sm text-[#586069]">Asset Class</span>
                  <span className="text-sm font-semibold text-[#24292E]">
                    {fund.asset_class}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#586069]">Risk Rating</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${getRiskColor(fund.risk_rating)}`}>
                    {fund.risk_rating}
                  </span>
                </div>
              </div>
            </div>

            {/* Performance */}
            <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
              <h3 className="text-lg font-semibold text-[#24292E] mb-4">Performance</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#586069]">YTD Return</span>
                  <span className={`text-sm font-semibold ${
                    fund.ytd_return >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                  }`}>
                    {formatPercent(fund.ytd_return)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#586069]">1-Year Return</span>
                  <span className={`text-sm font-semibold ${
                    fund.one_year_return >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                  }`}>
                    {formatPercent(fund.one_year_return)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#586069]">3-Year Return</span>
                  <span className={`text-sm font-semibold ${
                    fund.three_year_return >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                  }`}>
                    {formatPercent(fund.three_year_return)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#586069]">5-Year Return</span>
                  <span className={`text-sm font-semibold ${
                    fund.five_year_return >= 0 ? 'text-[#28A745]' : 'text-[#D73A49]'
                  }`}>
                    {formatPercent(fund.five_year_return)}
                  </span>
                </div>
              </div>
            </div>

            {/* Investment Action */}
            <div className="bg-[#F6F8FA] border border-[#E1E4E8] rounded-lg p-6">
              <h3 className="text-sm font-semibold text-[#24292E] mb-2">Ready to invest?</h3>
              <p className="text-xs text-[#586069] mb-4">
                Add {fund.symbol} to your portfolio with a minimum investment
              </p>
              <button
                onClick={handleInvest}
                className="w-full px-4 py-3 bg-[#0366D6] hover:bg-[#0256C5] text-white font-semibold rounded transition-colors"
              >
                Invest Now
              </button>
            </div>

            {/* Tier 1 Approved Notice */}
            <div className="bg-[#E6F7E6] border border-[#28A745] rounded-lg p-4">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-[#28A745] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-[#22863A] mb-1">
                    Tier 1 Approved Fund
                  </p>
                  <p className="text-xs text-[#22863A]">
                    This fund has been approved for federal retirement accounts and meets all regulatory requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Modal */}
      {orderModalOpen && (
        <OrderModal
          fund={fund}
          orderType="buy"
          onClose={handleOrderComplete}
        />
      )}
    </div>
  );
};
